import { useState, useRef, useEffect } from 'react';
import { Upload, Download, File, FileArchive, FileInput, Loader2, ChevronDown } from 'lucide-react';
import JSZip from 'jszip';
import { huffmanEncode, huffmanDecode } from '../utils/huffman';
import { lz77Encode, lz77Decode } from '../utils/lz77';

const FileCompressor = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [compressedFile, setCompressedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [algorithm, setAlgorithm] = useState('deflate');
  const [showAlgorithms, setShowAlgorithms] = useState(false);
  const fileInputRef = useRef(null);

  const algorithms = [
    { id: 'deflate', name: 'DEFLATE (ZIP)', description: 'Standard compression used in ZIP files' },
    { id: 'huffman', name: 'Huffman Coding', description: 'Efficient prefix coding algorithm' },
    { id: 'lz77', name: 'LZ77', description: 'Dictionary coder with sliding window' }
  ];

  const selectedAlgorithm = algorithms.find(a => a.id === algorithm) || algorithms[0];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setCompressedFile(null);
      setStatus('');
    }
  };

  const compressFile = async () => {
    if (!selectedFile) return;
    
    setIsLoading(true);
    setStatus(`Compressing file using ${selectedAlgorithm.name}...`);
    
    try {
      let result;
      const arrayBuffer = await selectedFile.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      switch (algorithm) {
        case 'deflate': {
          const zip = new JSZip();
          zip.file(selectedFile.name, uint8Array, { compression: 'DEFLATE' });
          const content = await zip.generateAsync({
            type: 'blob',
            compression: 'DEFLATE',
            compressionOptions: { level: 6 }
          });
          result = { name: `${selectedFile.name}.zip`, data: content };
          break;
        }
        
        case 'huffman': {
          const { encoded, tree } = huffmanEncode(uint8Array);
          const resultBlob = new Blob([JSON.stringify({ data: Array.from(encoded), tree })], { type: 'application/octet-stream' });
          result = { name: `${selectedFile.name}.huff`, data: resultBlob };
          break;
        }
        
        case 'lz77': {
          const encoded = lz77Encode(uint8Array);
          const resultBlob = new Blob([JSON.stringify(encoded)], { type: 'application/octet-stream' });
          result = { name: `${selectedFile.name}.lz77`, data: resultBlob };
          break;
        }
        
        default:
          throw new Error('Unsupported compression algorithm');
      }
      
      setCompressedFile(result);
      setStatus(`File compressed successfully using ${selectedAlgorithm.name}!`);
    } catch (error) {
      console.error('Compression error:', error);
      setStatus(`Error compressing file: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const decompressFile = async () => {
    if (!selectedFile) {
      setStatus('Please select a file for decompression');
      return;
    }
    
    setIsLoading(true);
    setStatus('Decompressing file...');
    
    try {
      let result;
      const arrayBuffer = await selectedFile.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      if (selectedFile.name.endsWith('.zip') || algorithm === 'deflate') {
        const zip = new JSZip();
        const zipFile = await zip.loadAsync(arrayBuffer);
        const fileName = Object.keys(zipFile.files)[0];
        if (!fileName) throw new Error('No files found in the zip');
        
        const fileData = await zipFile.file(fileName).async('blob');
        result = { name: fileName, data: fileData };
      } 
      else if (selectedFile.name.endsWith('.huff') || algorithm === 'huffman') {
        const text = new TextDecoder().decode(uint8Array);
        const { data, tree } = JSON.parse(text);
        const decoded = huffmanDecode(new Uint8Array(data), tree);
        const originalName = selectedFile.name.replace(/\.huff$/, '');
        result = { 
          name: originalName, 
          data: new Blob([decoded], { type: 'application/octet-stream' }) 
        };
      }
      else if (selectedFile.name.endsWith('.lz77') || algorithm === 'lz77') {
        const text = new TextDecoder().decode(uint8Array);
        const encoded = JSON.parse(text);
        const decoded = lz77Decode(encoded);
        const originalName = selectedFile.name.replace(/\.lz77$/, '');
        result = { 
          name: originalName, 
          data: new Blob([decoded], { type: 'application/octet-stream' }) 
        };
      }
      else {
        throw new Error('Unsupported file format for decompression');
      }
      
      setCompressedFile(result);
      setStatus('File decompressed successfully!');
    } catch (error) {
      console.error('Decompression error:', error);
      setStatus(`Error decompressing file: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFile = () => {
    if (!compressedFile) return;
    
    const url = URL.createObjectURL(compressedFile.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = compressedFile.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">File Compressor</h1>
          
          {/* Algorithm Selector */}
          <div className="relative">
            <button
              onClick={() => setShowAlgorithms(!showAlgorithms)}
              className="flex items-center space-x-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <span>{selectedAlgorithm.name}</span>
              <ChevronDown className="h-4 w-4" />
            </button>
            
            {showAlgorithms && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                <div className="p-2">
                  <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">COMPRESSION ALGORITHM</p>
                  {algorithms.map((algo) => (
                    <button
                      key={algo.id}
                      onClick={() => {
                        setAlgorithm(algo.id);
                        setShowAlgorithms(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm rounded-md ${
                        algorithm === algo.id
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="font-medium">{algo.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{algo.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {showAlgorithms && (
            <div 
              className="fixed inset-0 z-0" 
              onClick={() => setShowAlgorithms(false)}
            />
          )}
        </div>
        
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center mb-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <Upload className="h-12 w-12 text-gray-400" />
            <div className="text-sm text-gray-600 dark:text-gray-300">
              {selectedFile ? (
                <div className="flex items-center justify-center space-x-2">
                  <File className="h-4 w-4" />
                  <span className="font-medium text-gray-900 dark:text-white">{selectedFile.name}</span>
                  <span className="text-xs text-gray-500">
                    ({(selectedFile.size / 1024).toFixed(2)} KB)
                  </span>
                </div>
              ) : (
                <p>Drag and drop a file here, or click to select</p>
              )}
            </div>
            <button
              onClick={triggerFileInput}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Choose File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
          <div className="flex-1">
            <button
              onClick={compressFile}
              disabled={!selectedFile || isLoading}
              className={`w-full px-6 py-3 rounded-md flex items-center justify-center space-x-2 ${
                !selectedFile || isLoading
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <FileArchive className="h-4 w-4" />
                  <span>Compress with {selectedAlgorithm.name}</span>
                </>
              )}
            </button>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">
              {selectedAlgorithm.description}
            </p>
          </div>
          
          <div className="flex-1">
            <button
              onClick={decompressFile}
              disabled={!selectedFile || isLoading}
              className={`w-full px-6 py-3 rounded-md flex items-center justify-center space-x-2 ${
                !selectedFile || isLoading
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <FileInput className="h-4 w-4" />
                  <span>Decompress File</span>
                </>
              )}
            </button>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-center">
              {selectedFile ? `Detected format: ${
                selectedFile.name.endsWith('.zip') ? 'ZIP' : 
                selectedFile.name.endsWith('.huff') ? 'Huffman' :
                selectedFile.name.endsWith('.lz77') ? 'LZ77' : 'Unknown'
              }` : ' '}
            </p>
          </div>
        </div>

        {status && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-md">
            <p className="text-center">{status}</p>
          </div>
        )}

        {compressedFile && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <File className="h-5 w-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {compressedFile.name}
                </span>
                <span className="text-xs text-gray-500">
                  ({(compressedFile.data.size / 1024).toFixed(2)} KB)
                </span>
              </div>
              <button
                onClick={downloadFile}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileCompressor;
